import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle, Zap } from "lucide-react";
import axios from "axios";
import { trackPurchase as trackMetaPurchase, getMetaAttributionPayload } from "@/utils/metaPixel";
import { trackPurchase as trackGA4Purchase } from "@/utils/googleAnalytics";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  const { getHeaders } = useAuth();
  const sessionId = searchParams.get("session_id");
  const product = searchParams.get("product");
  const creditsParam = searchParams.get("credits");
  const abortRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { navigate("/results"); return; }
    abortRef.current = false;

    const trackingKey = `purchase_tracked_${sessionId}`;

    async function pollStatus(attempt) {
      if (abortRef.current) return;
      if (attempt >= 8) { setStatus("timeout"); return; }
      try {
        const res = await axios.get(`${API}/payment/status/${sessionId}`, {
          headers: getHeaders(),
          params: getMetaAttributionPayload(),
        });
        if (abortRef.current) return;

        if (res.data.payment_status === "paid") {
          setStatus("unlocking");

          // Track Purchase event ONCE per session_id using sessionStorage guard
          if (!sessionStorage.getItem(trackingKey)) {
            sessionStorage.setItem(trackingKey, "true");
            // Use the ACTUAL charged amount/currency from the transaction (multi-
            // currency: US pays $14/$500, India ₹99/₹2,499). Legacy INR figures
            // remain only as a fallback if the API omits the amount.
            let amount = res.data.amount;
            if (amount == null) {
              if (product === "season_pass") {
                amount = 2499;
              } else if (product === "credits") {
                amount = (parseInt(creditsParam || "0") / 5) * 10;
              } else {
                amount = 99;
              }
            }
            const currency = (res.data.currency || "INR").toUpperCase();
            const productName = product || "resume_analyse";
            
            const metaEventId = res.data.meta_event_id || `purchase_${sessionId}`;

            // Fire the browser Pixel Purchase with the same stable ID as backend CAPI.
            trackMetaPurchase(amount, currency, productName, { eventId: metaEventId, clientOnly: true });
            
            // Track in GA4
            trackGA4Purchase(
              sessionId,
              amount,
              productName,
              currency
            );
          }

          if (product === "season_pass") {
            setStatus("success");
            setTimeout(() => navigate("/dashboard"), 2500);
          } else if (product === "credits") {
            setStatus("success");
            setTimeout(() => navigate("/companion"), 2500);
          } else {
            // Task 7: Redirect to upsell page after ₹99 payment
            const analysisId = res.data.analysis_id || "";
            try {
              await axios.post(`${API}/analysis/${analysisId}/unlock`, {}, { headers: getHeaders() });
            } catch (e) { /* May already be unlocked */ }
            setStatus("success");
            setTimeout(() => {
              if (analysisId) {
                navigate(`/upsell/session-pass?analysis_id=${analysisId}`);
              } else {
                navigate("/results");
              }
            }, 1500);
          }
        } else {
          setTimeout(() => pollStatus(attempt + 1), 2000);
        }
      } catch (err) {
        if (!abortRef.current) setTimeout(() => pollStatus(attempt + 1), 2500);
      }
    }

    pollStatus(0);
    return () => { abortRef.current = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="ap-root ap-mesh min-h-screen pt-24 pb-24 flex items-center justify-center px-5"
      data-testid="payment-success-page"
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[460px] h-[460px] rounded-full blur-[130px] pointer-events-none"
        style={{
          background:
            (status === "success"
              ? "rgba(22,163,74,.10)"
              : status === "timeout"
              ? "rgba(255,107,53,.10)"
              : "rgba(91,61,245,.10)"),
        }}
      />

      <Card
        className="ap-card ap-rise w-full max-w-md relative z-10 overflow-hidden border-0"
        style={{ borderRadius: "22px" }}
        data-testid="payment-status-card"
      >
        <CardContent className="px-8 py-12 text-center">
          {(status === "checking" || status === "unlocking") && (
            <>
              <div
                className="relative w-20 h-20 mx-auto mb-7 rounded-full flex items-center justify-center"
                style={{ background: "var(--ap-violet-soft)" }}
              >
                <Loader2
                  className="w-20 h-20 animate-spin absolute"
                  style={{ color: "var(--ap-violet)", opacity: 0.85 }}
                />
                <Zap className="w-7 h-7" style={{ color: "var(--ap-violet)" }} />
              </div>
              <div className="ap-eyebrow mb-3">
                {status === "checking" ? "Confirming" : "Almost there"}
              </div>
              <h2
                className="text-2xl font-extrabold tracking-tight mb-2"
                style={{ color: "var(--ap-ink)" }}
              >
                {status === "checking" ? "Verifying Payment..." : "Generating Your Report..."}
              </h2>
              <p className="text-sm" style={{ color: "var(--ap-muted)" }}>
                {status === "checking" ? "Please wait while we confirm your payment." : "Our AI is writing your optimized resume."}
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div
                className="relative w-20 h-20 mx-auto mb-7 rounded-full flex items-center justify-center"
                style={{ background: "rgba(22,163,74,.12)" }}
              >
                <CheckCircle className="w-11 h-11" style={{ color: "var(--ap-green)" }} />
              </div>
              <div className="ap-eyebrow mb-3" style={{ color: "var(--ap-green)" }}>
                Success
              </div>
              <h2
                className="text-2xl font-extrabold tracking-tight mb-2"
                style={{ color: "var(--ap-ink)" }}
              >
                Payment <span className="ap-grad-text">Successful!</span>
              </h2>
              <p className="text-sm" style={{ color: "var(--ap-muted)" }}>
                {product === "credits" && creditsParam
                  ? `${creditsParam} AI credits added to your account. Redirecting...`
                  : product === "season_pass"
                  ? "Full Career OS activated! Redirecting..."
                  : "Your full analysis is ready. Redirecting..."}
              </p>
            </>
          )}

          {status === "timeout" && (
            <>
              <div
                className="relative w-20 h-20 mx-auto mb-7 rounded-full flex items-center justify-center"
                style={{ background: "rgba(255,107,53,.12)" }}
              >
                <XCircle className="w-11 h-11" style={{ color: "var(--ap-orange)" }} />
              </div>
              <div className="ap-eyebrow mb-3" style={{ color: "var(--ap-orange)" }}>
                Hold on
              </div>
              <h2
                className="text-2xl font-extrabold tracking-tight mb-2"
                style={{ color: "var(--ap-ink)" }}
              >
                Verification Timeout
              </h2>
              <p className="text-sm mb-7" style={{ color: "var(--ap-muted)" }}>
                We couldn't verify your payment. It may still be processing.
              </p>
              <button
                onClick={() => navigate("/results")}
                className="ap-btn ap-btn-fire ap-btn-md"
                data-testid="back-to-results-btn"
              >
                Back to Results
              </button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
